import { useState } from 'react'
import { Head } from '@inertiajs/react'
import AppLayout from '@/components/AppLayout.tsx'
import { Tabs, TabsContent } from '@/components/ui/Tabs'
import ReviewTabsList from '../TopDown/components/ReviewTabsList'
import { Button } from '@/components/ui/Button'
import PlanHeader from '../components/PlanHeader'
import ReviewSubmitPopover from '../TopDown/components/ReviewSubmitPopover'
import ReviewTaskCard from '../TopDown/components/ReviewTaskCard'
import CommentCard, { type CommentProps } from '../TopDown/components/CommentCard'
import CommentComposer from '../TopDown/components/CommentComposer'
import SubmissionCard, { type SubmissionProps } from '../TopDown/components/SubmissionCard'
import PricingTable from './components/PricingTable'
import { pricingChangeCount, pricingSummaryText, type PricingDiff } from './components/pricingDiff'
import type { PricingPageProps } from './pricingTypes'
import { type Person, type Task } from '@/pages/Tasks/task'
import { plan_product_pricings_index_path, tasks_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'

interface Props extends PricingPageProps {
  // What the submitted draft changes against the published rows; null when
  // the task was submitted with no draft open (nothing changed).
  pricing_diff: PricingDiff | null
  review_task: Task
  people: Person[]
  submission: SubmissionProps
  comments: CommentProps[]
}

// The reviewer's page for one channel's pricing submission, GitHub PR layout
// like Plan/TopDown/Review: the submitted draft rendered read-only with
// green/red diff marks against the published pricings, the submission message
// as a timeline comment, the task as a sidebar. The verdict lives in the app
// header's Submit review popover.
export default function ProductPricingsReview({ plan, price_levels, channel, groups, pricing_diff, review_task, people, submission, comments }: Props) {
  const market = useMarket()
  const [changesOnly, setChangesOnly] = useState(true)
  const currency = plan.currency ?? market.currency
  const showPath = plan_product_pricings_index_path(market.slug, channel ? { channel: channel.slug } : {})

  return (
    <AppLayout
      header={<PlanHeader
        name={plan.name}
        orgNodeName={plan.org_node?.name ?? null}
        revenueTarget={plan.revenue_target}
        currency={plan.currency}
        beginDate={plan.begin_date}
        endDate={plan.end_date}
      />}
      headerActions={submission.can_review && (
        <ReviewSubmitPopover
          submissionId={submission.id}
          planName={`${channel?.name ?? ''} pricings`}
          revision={pricing_diff != null}
          approveBackTo={showPath}
          sendBackBackTo={tasks_path()}
        />
      )}
    >
      <Head title={`${channel?.name ?? plan.name} pricings — Review`} />
      <div
        className="mb-4 rounded-lg border px-4 py-2.5 text-sm text-foreground"
        style={{ background: 'var(--info-surface)', borderColor: 'var(--border)' }}
      >
        <span className="font-semibold">{channel?.name} · Products &amp; Pricings.</span>{' '}
        {pricing_diff ? pricingSummaryText(pricing_diff.summary) : 'No changes against the published pricings'}.
        {pricing_diff?.summary.any_changes && ' In the Plan changes tab, green is what this submission adds and red is the published value it replaces.'}
        {pricing_diff && pricing_diff.levels.removed.length > 0 && (
          <> Price levels removed: {pricing_diff.levels.removed.map((l) => l.name).join(', ')}.</>
        )}
      </div>
      <Tabs defaultValue="conversation">
        <ReviewTabsList commentCount={comments.length} changeCount={pricing_diff ? pricingChangeCount(pricing_diff.summary) : null} />
        <TabsContent value="conversation">
          <div className="flex items-start gap-4">
            <div className="flex min-w-0 flex-col gap-4 lg:w-3/5">
              <SubmissionCard submission={submission} />
              {comments.map((comment) => (
                <CommentCard key={comment.id} comment={comment} submissionId={submission.id} />
              ))}
              <CommentComposer submissionId={submission.id} />
            </div>
            <ReviewTaskCard task={review_task} people={people} />
          </div>
        </TabsContent>
        <TabsContent value="changes">
          <div className="overflow-hidden rounded-md border bg-card">
            <div className="flex items-center justify-between border-b px-4 py-2 text-[12px] text-muted-foreground">
              <span>{channel?.name}</span>
              {pricing_diff && (
                <Button variant="ghost" size="sm" className="h-7 text-[12px]" onClick={() => setChangesOnly((v) => !v)}>
                  {changesOnly ? 'Show all rows' : 'Show changes only'}
                </Button>
              )}
            </div>
            <PricingTable
              groups={groups}
              priceLevels={price_levels}
              canEdit={false}
              emptyMessage={changesOnly ? 'No changed rows.' : `No products for ${channel?.name ?? 'this channel'}.`}
              diff={pricing_diff}
              changesOnly={changesOnly}
              currency={currency}
            />
          </div>
        </TabsContent>
      </Tabs>
    </AppLayout>
  )
}
